export const PARSLEY_CAPABILITY = 'parsley'
export const PARSLEY_PATH = '/store/parsley'
export const PARSLEY_NEW_ITEMS_PATH = '/store/parsley/new'

// The API refuses more. Each created product costs a Parsley request of about
// two seconds.
export const MAX_ITEMS_PER_CREATE_RUN = 100

const LINK_STATUS_LABELS = {
  synced: 'Synced',
  pending: 'Waiting to sync',
  incomplete: 'Synced, but Parsley marks the nutrition incomplete',
  error: 'Sync failed',
  not_in_parsley: 'No longer in Parsley'
}
const ATTENTION_STATUSES = ['incomplete', 'error', 'not_in_parsley']
const ACTIVE_RUN_STATUSES = ['pending', 'running']
const SUMMARY_ORDER = ['created', 'updated', 'unchanged', 'skipped', 'failed']

export const hasParsley = user => Boolean(user?.attributes?.capabilities?.includes(PARSLEY_CAPABILITY))

export const linkStatusLabel = status => LINK_STATUS_LABELS[status] ?? LINK_STATUS_LABELS.pending

export const linkNeedsAttention = status => ATTENTION_STATUSES.includes(status)

const itemTypeLabel = type => (type === 'subrecipe' ? 'Subrecipe' : 'Recipe')

// The serving basis is what tells a plated dish from its cooked component:
// they share a name and carry different nutrition.
export const itemDescription = item =>
  [itemTypeLabel(item.item_type), item.serving_basis_label && `nutrition ${item.serving_basis_label}`]
    .filter(Boolean)
    .join(', ')

export const priceText = cents =>
  cents === null || cents === undefined ? 'No price in Parsley' : `$${(cents / 100).toFixed(2)}`

const isPresent = value => value !== null && value !== undefined && value !== ''

// Parsley items often share a name, and linking the wrong one publishes the
// wrong allergens. The id, price and tags are what tell them apart.
export const itemIdentity = item =>
  [isPresent(item.parsley_id) && `Parsley #${item.parsley_id}`, priceText(item.price_cents), ...(item.tags ?? [])]
    .filter(Boolean)
    .join(', ')

export const formatDateTime = iso =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : ''

export const runIsActive = run => ACTIVE_RUN_STATUSES.includes(run?.attributes?.status)

export const runSummaryText = summary => {
  const parts = SUMMARY_ORDER.filter(key => summary?.[key] > 0).map(key => `${summary[key]} ${key}`)
  return parts.length ? parts.join(', ') : 'Nothing to update'
}

export const runHeadline = run => {
  if (!run) return 'Never synced'
  const { status, kind, error_message: error, finished_at: finishedAt, summary } = run.attributes
  if (runIsActive(run))
    return kind === 'create_products' ? 'Creating products from Parsley...' : 'Syncing with Parsley...'
  if (status === 'failed') return error || 'The last run failed'
  return `${runSummaryText(summary)}. Finished ${formatDateTime(finishedAt)}`
}

// The API offsets without clamping the page, so a change that empties the
// page being viewed (the last row on it linked, unlinked, or turned into a
// product, say) can leave the merchant stranded on a page past the end.
export const isPastLastPage = (result, page) => result.data.length === 0 && page > 1 && result.total_pages < page

export const isParsleyLocked = product => Boolean(product?.attributes?.parsley_link?.locked)

export const isContainsLabel = label => label?.attributes?.label_type === 'contains'

// A later, unrelated request (the autofill fallback product search, for one) can overwrite the
// store's copy of a product with a version serialized without the parsley_link attribute. That
// omission means nothing about the link; it just means that response never asked about Parsley.
// Only a record that carries the key at all (an object, or null) can change what a page
// remembers about the product it is editing.
export const nextParsleyLink = (previous, product) => {
  const attributes = product?.attributes
  if (!attributes || !('parsley_link' in attributes)) return previous
  return attributes.parsley_link ?? null
}
